import apiClient from "@/api/apiClient";
import type { CompanyLookupResponse, CompanyRevenuePrediction } from '@/types/company';

const companyApi = {
  // Combined has-access + auto-acquire(+bill) + fetch, in one round trip.
  ensureGeneralData: async (cnpj: string): Promise<any> => {
    return await apiClient.get(`/receita/general`, { params: { cnpj8: cnpj } });
  },
  // Lightweight, branch-accurate identity + location (nome / cidade / uf) for a full CNPJ.
  // Free Receita data, not entitlement-gated — used by SAR envolvido auto-fill.
  getReceitaIdentityLocation: async (
    cnpj14: string,
  ): Promise<{ nome: string | null; cidade: string | null; uf: string | null }> => {
    const response = await apiClient.get(`/receita/identity-location`, { params: { cnpj14 } });
    return response?.data ?? response;
  },
  getCompanyByCnpjOrName: async (input: string, offset: number, limit: number, only_active: boolean, search_type: string): Promise<CompanyLookupResponse> => {
    return await apiClient.get(`/company/search`, { params: { input, offset, limit, only_active, search_type } });
  },

  // Predicted annual revenue band from our own model: free, not entitlement-gated.
  getRevenuePrediction: async (cnpj8: string): Promise<CompanyRevenuePrediction> => {
    return await apiClient.get(`/company/${cnpj8}/revenue-prediction`);
  },

  // --- Network Explorer Endpoints ---
  getNetworkSummary: async (cnpj8: string): Promise<any> => {
    return await apiClient.get(`/receita/${cnpj8}/network/summary`);
  },
  getBranchesList: async (
    cnpj8: string,
    page: number = 1,
    limit: number = 50,
    filters: { search?: string; uf?: string; situacao?: string; cnae?: string } = {},
  ): Promise<any> => {
    const params: any = { page, limit };
    if (filters.search) params.search = filters.search;
    if (filters.uf) params.uf = filters.uf;
    if (filters.situacao) params.situacao = filters.situacao;
    if (filters.cnae) params.cnae = filters.cnae;
    return await apiClient.get(`/receita/${cnpj8}/branches`, { params });
  },
  getBranchDetail: async (cnpj14: string): Promise<any> => {
    return await apiClient.get(`/receita/branch/${cnpj14}`);
  },
  
  // --- DUNS Number Endpoint ---
  getDunsNumber: async (cnpj14: string): Promise<{ duns_number: string | null, cnpj14: string }> => {
    try {
      const response = await apiClient.get(`/company/duns/${cnpj14}`);
      return response?.data || response || { duns_number: null, cnpj14 };
    } catch (error) {
      // Return null duns_number on error (non-blocking)
      console.error('Error fetching DUNS number:', error);
      return { duns_number: null, cnpj14 };
    }
  }
};

export default companyApi;
